import SessionStorage from './SessionStorage';
import AxiosService from '@/services/api/AxiosService';
import { UserSettings } from '@/entities/UserSettings';
import ApiLinks from '@/entities/ApiLinks';
import ApiStatus from '@/entities/enums/apiStatuses';
import { DepartmentUserSettings } from '@/entities/DepartmentUserSettings';
import { vxm } from '@/store/store.vuex';
import i18n from '@/lang/lang';
import { AdminPermissions } from '../permissions/AdminPermissions';
import LoggedUserManager from '../LoggedUserManager';
const userCan = LoggedUserManager.checkPermissions;
var vxDepartmentStore = vxm.departmentStore;
const MAX_SETTINGS_AGE = 60000 * 60;
/**
 * Singleton repository
 */
export default class UserSettingsRepository {
    LS_TAG = SessionStorage.keys.SETTINGS;
    LS_DEPARTMENT_TAG = SessionStorage.keys.USER_DEPARTMENT_SETTINGS;
    static _instance;
    _responsePromise = null;
    constructor() { }
    static getInstance(reload = false) {
        if (!this._instance || reload) {
            this._instance = new this();
        }
        return this._instance;
    }
    async loadUserSettings(reload = false) {
        let cachedData = SessionStorage.loadData(this.LS_TAG);
        if (cachedData?.lastUpdated + MAX_SETTINGS_AGE < Date.now())
            reload = true;
        if (!reload && cachedData?.data)
            return UserSettings.fromApi(cachedData.data);
        if (!this._responsePromise) {
            let url = ApiLinks.Profile.Settings;
            this._responsePromise = AxiosService.getDataFromServer(url);
        }
        let response = await this._responsePromise;
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else {
            let settings = UserSettings.fromApi(response.data.data.profile);
            SessionStorage.storeData(this.LS_TAG, settings);
            this._responsePromise = null;
            return settings;
        }
    }
    async adminLoadUserSettings(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_SETTINGS_READ)) {
            throw 'Forbidden action adminLoadUserSettings';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Settings}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else
            return UserSettings.fromApi(response.data.data.profile);
    }
    async saveUserSettings(keys, values) {
        let path;
        let userSettings = [];
        keys.forEach((key, i) => {
            path = key.join('.');
            userSettings.push({ Key: path, Value: values[i] });
        });
        let url = ApiLinks.Profile.Settings;
        let response = await AxiosService.postDataToServer(url, userSettings);
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else {
            let settings = UserSettings.fromApi(response.data.data.profile);
            SessionStorage.storeData(this.LS_TAG, settings);
            return settings;
        }
    }
    async loadUserSettingsForDepartment(userId, reload = false, departmentId = this.getSelectedDepartmentId()) {
        let cachedData = SessionStorage.loadData(this.LS_DEPARTMENT_TAG);
        if (cachedData?.lastUpdated + MAX_SETTINGS_AGE < Date.now())
            reload = true;
        if (!reload && cachedData?.data)
            return DepartmentUserSettings.fromApi(cachedData.data);
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.Settings}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else {
            let settings = DepartmentUserSettings.fromApi(response.data.data[departmentId]);
            SessionStorage.storeData(this.LS_DEPARTMENT_TAG, settings);
            return settings;
        }
    }
    async saveUserSettingForDepartment(userId, key, value, departmentId = this.getSelectedDepartmentId()) {
        let path = key.join('.');
        let userSetting = [{ Key: path, Value: value }];
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.Settings}`;
        let response = await AxiosService.postDataToServer(url, userSetting);
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else {
            let settings = DepartmentUserSettings.fromApi(response.data.data[departmentId]);
            SessionStorage.storeData(this.LS_DEPARTMENT_TAG, settings);
            return settings;
        }
    }
    async saveUserSettingsForDepartment(userId, changes, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.Settings}`;
        let response = await AxiosService.postDataToServer(url, changes);
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else
            return response;
    }
    async adminSaveUserSettings(userId, changes) {
        if (!userCan(AdminPermissions.ADMIN_USERS_SETTINGS_UPDATE)) {
            throw 'Forbidden action adminSaveUserSettings';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Settings}`;
        let response = await AxiosService.postDataToServer(url, changes);
        if (response?.status != ApiStatus.SUCCESS)
            throw i18n.t('error_messages.fatal_error');
        else
            return response;
    }
    async removeUserSetting(userId, key, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.Settings}/${key}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async adminRemoveUserSetting(userId, key) {
        if (!userCan(AdminPermissions.ADMIN_USERS_SETTINGS_DELETE)) {
            throw 'Forbidden action adminRemoveUserSetting';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Settings}/${key}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    getSelectedDepartmentId() {
        return vxDepartmentStore.selectedDepartment.id;
    }
}
